import React, { useState } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { SoWhatCallout } from '../../components/funnel/SoWhatCallout';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { FoodSource } from '../../types';

interface FoodOption {
  key: FoodSource;
  labelKey: string;
  label: string;
  imageKey: string;
  imagePath: string;
}

const FOOD_OPTIONS: FoodOption[] = [
  {
    key: 'pienso_seco',
    labelKey: 'p6.food_seca',
    label: 'Seca',
    imageKey: 'p6.image.seca',
    imagePath: '/images/funnel/food_seca.png',
  },
  {
    key: 'comida_humeda',
    labelKey: 'p6.food_humeda',
    label: 'Húmeda',
    imageKey: 'p6.image.humeda',
    imagePath: '/images/funnel/food_humeda.png',
  },
  {
    key: 'barf',
    labelKey: 'p6.food_barf',
    label: 'BARF',
    imageKey: 'p6.image.barf',
    imagePath: '/images/funnel/food_barf.png',
  },
  {
    key: 'comida_cocinada',
    labelKey: 'p6.food_cocinada',
    label: 'Cocinada',
    imageKey: 'p6.image.cocinada',
    imagePath: '/images/funnel/food_cocinada.png',
  },
  {
    key: 'deshidratada',
    labelKey: 'p6.food_deshidratada',
    label: 'Deshidratada',
    imageKey: 'p6.image.deshidratada',
    imagePath: '/images/funnel/food_deshidratada.png',
  },
  {
    key: 'mixta',
    labelKey: 'p6.food_mixta',
    label: 'Mixta',
    imageKey: 'p6.image.mixta',
    imagePath: '/images/funnel/food_mixta.png',
  },
];

// Dietas donde el perro ya come natural/húmedo → no necesita transición progresiva.
const NO_TRANSITION_FOODS: FoodSource[] = ['comida_humeda', 'comida_cocinada', 'natural_cocinada', 'barf'];
const TRANSITION_REQUIRED_FOODS: FoodSource[] = ['pienso_seco', 'mixta'];

interface FoodIconProps {
  imageKey: string;
  imagePath: string;
  alt: string;
}

const FoodIcon: React.FC<FoodIconProps> = ({ imageKey, imagePath, alt }) => {
  const { t } = useAppConfig();
  const overrideUrl = t(imageKey, '').trim();
  const src = overrideUrl || imagePath;
  return (
    <img
      src={src}
      alt={alt}
      className="mb-1 h-20 w-20 object-contain sm:mb-3 sm:h-36 sm:w-36 lg:mb-2 lg:h-24 lg:w-24"
    />
  );
};

const P6ComidaApetito: React.FC = () => {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { t } = useAppConfig();

  const nombre = formData.nombre || 'tu perro';

  const [currentFood, setCurrentFood] = useState<FoodSource[]>(() => {
    const raw = formData.currentFood ?? [];
    if (raw.includes('no_lo_se')) return ['no_lo_se'];
    return raw;
  });
  const [attempted, setAttempted] = useState(false);

  const canContinue = currentFood.length > 0;
  const missingReason = !canContinue ? t('p6.missing_food', 'Indica qué come {nombre} ahora', { nombre }) : undefined;

  function toggleFood(key: FoodSource): void {
    const isNoLoSe = key === 'no_lo_se';
    const withoutNoLoSe = currentFood.filter((k) => k !== 'no_lo_se');
    let next: FoodSource[];
    if (isNoLoSe) {
      next = currentFood.includes('no_lo_se') ? [] : ['no_lo_se'];
    } else if (withoutNoLoSe.includes(key)) {
      next = withoutNoLoSe.filter((k) => k !== key);
    } else {
      next = [...withoutNoLoSe, key];
    }
    setCurrentFood(next);
    const skipTransition =
      next.length > 0 &&
      next.some((f) => NO_TRANSITION_FOODS.includes(f)) &&
      !next.some((f) => TRANSITION_REQUIRED_FOODS.includes(f));
    updateFormData({
      currentFood: next,
      subscriptionStart: skipTransition ? 'full' : 'transition',
    });
  }

  const whyCallout = currentFood.includes('no_lo_se') ? (
    <SoWhatCallout>
      {t(
        'p6.why_no_lo_se',
        'Sin problema — aplicamos la transición estándar para que el sistema digestivo de {nombre} se adapte sin sustos.',
        { nombre },
      )}
    </SoWhatCallout>
  ) : (
    <SoWhatCallout>
      {t('p6.why', 'Selecciona qué come {nombre} hoy para adaptar la receta y el ritmo de transición.', { nombre })}
    </SoWhatCallout>
  );

  return (
    <FunnelLayout
      step={6}
      useTwoPhase
      onContinue={goToNextStep}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      onBack={goToPreviousStep}
      sidebar={whyCallout}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <h1 className="font-fredoka text-xl sm:text-2xl font-semibold text-center text-black">
            <span className="mr-1" aria-hidden="true">
              🍽️
            </span>
            {t('p6.food_title', '¿Qué come {nombre} ahora?', { nombre })}
          </h1>
          <p className="font-poppins text-xs text-center text-black/50">
            {t('p6.food_hint', 'Selecciona una o varias opciones')}
          </p>

          <div
            className={`grid grid-cols-2 gap-2 sm:gap-5 lg:grid-cols-3 lg:gap-3 ${attempted && !canContinue ? 'ring-2 ring-red-400 ring-offset-2 rounded-[20px]' : ''}`}
          >
            {FOOD_OPTIONS.map((opt) => {
              const isActive = currentFood.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => toggleFood(opt.key)}
                  className={`relative flex h-28 flex-col items-center justify-center rounded-[18px] px-2 py-2 transition-all duration-200 sm:h-52 sm:rounded-[20px] sm:px-3 sm:py-4 lg:h-36 lg:py-3 ${
                    isActive
                      ? 'bg-white border-[2.5px] border-[#008BBC] shadow-[0_0_0_4px_rgba(0,139,188,0.15)] scale-[1.03]'
                      : 'bg-white/70 border-2 border-transparent hover:border-[#90D1E8] hover:scale-105 hover:shadow-md'
                  }`}
                >
                  {isActive && (
                    <span
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center text-white text-xs font-bold leading-none shadow z-20"
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                  )}

                  <FoodIcon imageKey={opt.imageKey} imagePath={opt.imagePath} alt="" />

                  <span
                    className={`font-fredoka text-sm font-semibold text-center leading-tight ${
                      isActive ? 'text-[#008BBC]' : 'text-black/70'
                    }`}
                  >
                    {t(opt.labelKey, opt.label)}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            aria-pressed={currentFood.includes('no_lo_se')}
            onClick={() => toggleFood('no_lo_se')}
            className={`flex min-h-[50px] w-full flex-row items-center justify-center gap-2 rounded-full border-2 px-5 py-3 font-fredoka text-base font-semibold transition-all duration-200 ${
              currentFood.includes('no_lo_se')
                ? 'bg-white border-[#008BBC] text-[#008BBC] shadow-[0_0_0_4px_rgba(0,139,188,0.15)]'
                : 'bg-white border-[#E9E3D7] text-black/65 hover:border-[#90D1E8] hover:text-black/80'
            }`}
          >
            <span className="shrink-0 text-lg leading-none" aria-hidden="true">
              🤷
            </span>
            <span className="whitespace-nowrap leading-none">{t('p6.food_no_lo_se', 'No lo sé')}</span>
          </button>
        </div>

        <div className="lg:hidden">{whyCallout}</div>
      </div>
    </FunnelLayout>
  );
};

export default P6ComidaApetito;
